export default {
  // ── Common ──────────────────────────────────────────────────────────────
  common: {
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    loading: 'Loading…',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    submit: 'Submit',
    approve: 'Approve',
    reject: 'Reject',
    view: 'View',
    details: 'Details',
    status: 'Status',
    date: 'Date',
    from: 'From',
    to: 'To',
    notes: 'Notes',
    actions: 'Actions',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    none: 'None',
    hours: 'hours',
    minutes: 'minutes',
    days: 'days',
    na: 'N/A',
    noData: 'No data available',
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    refresh: 'Refresh',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    page: 'Page',
    of: 'of',
    total: 'Total',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    department: 'Department',
    position: 'Position',
    employee: 'Employee',
    amount: 'Amount',
    reason: 'Reason',
    type: 'Type',
    duration: 'Duration',
    startDate: 'Start Date',
    endDate: 'End Date',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    language: 'Language',
    english: 'English',
    arabic: 'Arabic',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    active: 'Active',
    inactive: 'Inactive',
    completed: 'Completed',
    open: 'Open',
    closed: 'Closed',
    system: 'System',
    download: 'Download',
    upload: 'Upload',
    preview: 'Preview',
    generate: 'Generate',
    reset: 'Reset',
    apply: 'Apply',
    clear: 'Clear',
    copy: 'Copy',
    noResults: 'No results found',
    showMore: 'Show more',
    showLess: 'Show less',
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    summary: 'Summary',
    report: 'Report',
    description: 'Description',
    optional: 'optional',
    required: 'Required',
    invalidEmail: 'Enter a valid email address',
  },

  // ── Auth / Login ─────────────────────────────────────────────────────────
  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    signingIn: 'Signing in…',
    employee: 'Employee',
    admin: 'Admin',
    emailAddress: 'Email address',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    rememberMe: 'Remember me for 30 days',
    employeeSubtitle: 'Access your attendance dashboard',
    adminSubtitle: 'Access the admin control panel',
    loginSuccess: 'Login successful! Redirecting…',
    loginFailed: 'Login failed. Please try again.',
    unexpectedError: 'An unexpected error occurred. Please try again.',
    emailRequired: 'Email is required',
    passwordRequired: 'Password is required',
    passwordMinLength: 'Password must be at least 3 characters',
    enterPassword: 'Enter your password',
    allSystemsOperational: 'All systems operational',
    resetPassword: 'Reset password',
    resetInstructions: "Enter your email and we'll send you a link to reset your password.",
    sendLink: 'Send link',
    resetLinkSent: 'Password reset link sent to your email!',
    getAndroidApp: 'Get the Android app',
    secure: 'Secure',
    encrypted: 'Encrypted',
    protected: 'Protected',
    tagline: 'Everything your team needs, in one place.',
    taglineDesc: 'Real-time attendance tracking, leave management, overtime approvals, and payroll all unified.',
    featAttendance: 'Real-time Attendance',
    featAttendanceDesc: 'Clock in and out with GPS verification and live session tracking.',
    featLeave: 'Leave Management',
    featLeaveDesc: 'Request, approve, and track all leave types in one workflow.',
    featReports: 'Reports & Analytics',
    featReportsDesc: 'Attendance trends, overtime hours, and department breakdowns.',
    featPayroll: 'Payroll Integration',
    featPayrollDesc: 'Automatic deductions and payslip generation based on attendance data.',
  },

  // ── Navigation ───────────────────────────────────────────────────────────
  nav: {
    // Employee
    dashboard: 'Dashboard',
    attendance: 'Attendance',
    history: 'History',
    leaveRequests: 'Leave Requests',
    payslips: 'Payslips',
    profile: 'Profile',
    faceEnrollment: 'Face Enrollment',
    getTheApp: 'Get the App',
    overtimeRequests: 'Overtime Requests',
    userManual: 'Help & Manual',
    profileSettings: 'Profile Settings',
    // Short names (mobile)
    home: 'Home',
    clock: 'Clock',
    leave: 'Leave',
    pay: 'Pay',
    app: 'App',
    // Admin groups
    overview: 'Overview',
    workforce: 'Workforce',
    timeTracking: 'Time Tracking',
    leaveManagement: 'Leave',
    configuration: 'Configuration',
    // Admin items
    employees: 'Employees',
    payroll: 'Payroll',
    overtimeClock: 'Overtime Clock',
    overtimeManagement: 'Overtime Management',
    shortLeaveSessions: 'Short Leave Sessions',
    lateArrivalDebts: 'Late Arrival Debts',
    leaveManagementAdmin: 'Leave Management',
    leaveBalances: 'Leave Balances',
    holidays: 'Holidays',
    reports: 'Reports',
    departments: 'Departments',
    leaveTypes: 'Leave Types',
    balanceBanks: 'Balance Banks',
    workingHours: 'Working Hours',
    settings: 'Settings',
    autoApproval: 'Auto-Approval',
    locations: 'Locations',
    liveMap: 'Live Map',
    analytics: 'Analytics',
    sessions: 'Sessions',
    adminPortal: 'Admin Portal',
    employeePortal: 'Employee Portal',
  },

  // ── Employee Dashboard ───────────────────────────────────────────────────
  dashboard: {
    welcomeBack: 'Welcome back',
    todayOverview: "Today's Overview",
    clockedIn: 'Clocked In',
    clockedOut: 'Clocked Out',
    workingHours: 'Working Hours',
    leaveBalance: 'Leave Balance',
    upcomingLeave: 'Upcoming Leave',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    clockIn: 'Clock In',
    clockOut: 'Clock Out',
    requestLeave: 'Request Leave',
    viewPayslip: 'View Payslip',
    attendanceSummary: 'Attendance Summary',
    overtimeSummary: 'Overtime Summary',
    presentDays: 'Present Days',
    absentDays: 'Absent Days',
    lateDays: 'Late Days',
    leaveDays: 'Leave Days',
    pendingRequests: 'Pending Requests',
    thisMonth: 'This Month',
    noActivity: 'No recent activity',
    greetingMorning: 'Good morning',
    greetingAfternoon: 'Good afternoon',
    greetingEvening: 'Good evening',
    notifications: 'Notifications',
    noNotifications: 'No notifications',
    markAllRead: 'Mark all as read',
    annualLeave: 'Annual Leave',
    casualLeave: 'Casual Leave',
    sickLeave: 'Sick Leave',
    daysRemaining: 'days remaining',
    hoursWorked: 'Hours Worked',
    overtimeHours: 'Overtime Hours',
    nextPayslip: 'Next Payslip',
    lastClockIn: 'Last Clock-in',
  },

  // ── Attendance ───────────────────────────────────────────────────────────
  attendance: {
    title: 'Attendance',
    clockIn: 'Clock In',
    clockOut: 'Clock Out',
    leaveIn: 'Leave In',
    leaveOut: 'Leave Out',
    currentSession: 'Current Session',
    sessionDuration: 'Session Duration',
    location: 'Location',
    photo: 'Photo',
    takePhoto: 'Take Photo',
    retakePhoto: 'Retake',
    gps: 'GPS Location',
    gpsAccuracy: 'GPS Accuracy',
    gettingLocation: 'Getting location…',
    locationRequired: 'Location is required',
    photoRequired: 'Photo is required',
    submitClockIn: 'Submit Clock-In',
    submitClockOut: 'Submit Clock-Out',
    submitLeaveIn: 'Submit Leave-In',
    submitLeaveOut: 'Submit Leave-Out',
    leaveOutFirst: 'Leave Out first',
    onShortLeave: 'On Short Leave',
    shortLeaveTimer: 'Short Leave Duration',
    autoClockOut: 'Auto Clock-Out',
    autoClockOutBanner: 'Clocked out automatically by the system',
    missedClockOut: 'Missing clock-out',
    overtimeMode: 'Overtime Mode',
    overtimeActive: 'Overtime Active',
    startOvertime: 'Start Overtime',
    stopOvertime: 'Stop Overtime',
    history: 'Attendance History',
    todayRecord: "Today's Record",
    clockInTime: 'Clock-In Time',
    clockOutTime: 'Clock-Out Time',
    workHours: 'Work Hours',
    breakTime: 'Break Time',
    lateArrival: 'Late Arrival',
    earlyDeparture: 'Early Departure',
    approvalStatus: 'Approval Status',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    noRecordToday: 'No attendance record for today',
    attendanceRecords: 'Attendance Records',
    filterByDate: 'Filter by Date',
    filterByStatus: 'Filter by Status',
    clockInPhoto: 'Clock-In Photo',
    clockOutPhoto: 'Clock-Out Photo',
    viewOnMap: 'View on Map',
    inProgress: 'In Progress',
    gracePeriod: '(within grace period)',
  },

  // ── Leave Requests ───────────────────────────────────────────────────────
  leave: {
    title: 'Leave Requests',
    newRequest: 'New Request',
    requestLeave: 'Request Leave',
    leaveType: 'Leave Type',
    startDate: 'Start Date',
    endDate: 'End Date',
    numberOfDays: 'Number of Days',
    reason: 'Reason',
    attachDocument: 'Attach Document',
    documentRequired: 'Document Required',
    submitting: 'Submitting…',
    submitRequest: 'Submit Request',
    myRequests: 'My Leave Requests',
    leaveBalance: 'Leave Balance',
    annualLeave: 'Annual Leave',
    casualLeave: 'Casual Leave',
    sickLeave: 'Sick Leave',
    available: 'Available',
    used: 'Used',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
    cancelRequest: 'Cancel Request',
    confirmCancel: 'Are you sure you want to cancel this leave request?',
    requestCancelled: 'Leave request cancelled successfully',
    noRequests: 'No leave requests found',
    uploadDocument: 'Upload Document',
    removeDocument: 'Remove Document',
    viewDocument: 'View Document',
    daysBalance: 'days balance',
    requestSubmitted: 'Leave request submitted successfully',
    requestFailed: 'Failed to submit leave request',
    shortLeave: 'Short Leave',
    halfDay: 'Half Day',
    fullDay: 'Full Day',
    leaveHistory: 'Leave History',
    upcoming: 'Upcoming',
    past: 'Past',
    filterStatus: 'Filter by Status',
    selectLeaveType: 'Select leave type',
    balanceSummary: 'Balance Summary',
    expiryDate: 'Expiry Date',
    carryOver: 'Carry Over',
    totalAllocated: 'Total Allocated',
    totalUsed: 'Total Used',
    remaining: 'Remaining',
  },

  // ── Overtime ─────────────────────────────────────────────────────────────
  overtime: {
    title: 'Overtime Requests',
    newRequest: 'New Overtime Request',
    date: 'Date',
    hours: 'Hours',
    reason: 'Reason',
    status: 'Status',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
    cancelRequest: 'Cancel Request',
    noRequests: 'No overtime requests found',
    requestSubmitted: 'Overtime request submitted successfully',
    totalHours: 'Total Hours',
    estimatedPay: 'Estimated Pay',
    actualPay: 'Actual Pay',
    sessionStart: 'Session Start',
    sessionEnd: 'Session End',
    duration: 'Duration',
    clockIn: 'Clock In (Overtime)',
    clockOut: 'Clock Out (Overtime)',
    activeSession: 'Active Overtime Session',
    history: 'Overtime History',
    thisMonth: 'This Month',
    type: 'Type',
    legacy: 'Legacy',
    clockBased: 'Clock-Based',
  },

  // ── Payslips ─────────────────────────────────────────────────────────────
  payslips: {
    title: 'Payslips',
    period: 'Period',
    basicSalary: 'Basic Salary',
    grossPay: 'Gross Pay',
    netPay: 'Net Pay',
    deductions: 'Deductions',
    additions: 'Additions',
    overtimePay: 'Overtime Pay',
    absentDeduction: 'Absent Deduction',
    lateDeduction: 'Late Arrival Deduction',
    shortLeaveDeduction: 'Short Leave Deduction',
    earlyDepartureDeduction: 'Early Departure Deduction',
    manualAdjustment: 'Manual Adjustment',
    status: 'Status',
    draft: 'Draft',
    finalized: 'Finalized',
    paid: 'Paid',
    downloadPDF: 'Download PDF',
    viewDetails: 'View Details',
    noPayslips: 'No payslips found',
    generatedOn: 'Generated On',
    payslipFor: 'Payslip for',
    companyInfo: 'Company Information',
    employeeInfo: 'Employee Information',
    salaryBreakdown: 'Salary Breakdown',
    deductionBreakdown: 'Deduction Breakdown',
    attendanceSummary: 'Attendance Summary',
    workingDays: 'Working Days',
    presentDays: 'Present Days',
    absentDays: 'Absent Days',
    lateArrivals: 'Late Arrivals',
    onTimeArrivals: 'On-Time Arrivals',
    hourlyRate: 'Hourly Rate',
    monthlyHours: 'Monthly Hours',
  },

  // ── Profile ──────────────────────────────────────────────────────────────
  profile: {
    title: 'Profile',
    personalInfo: 'Personal Information',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    department: 'Department',
    position: 'Position',
    employeeId: 'Employee ID',
    hireDate: 'Hire Date',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    saveChanges: 'Save Changes',
    updateSuccess: 'Profile updated successfully',
    updateFailed: 'Failed to update profile',
    passwordChanged: 'Password changed successfully',
    passwordMismatch: 'Passwords do not match',
    profilePhoto: 'Profile Photo',
    uploadPhoto: 'Upload Photo',
    removePhoto: 'Remove Photo',
    salary: 'Salary',
    joinedOn: 'Joined On',
  },

  // ── History ──────────────────────────────────────────────────────────────
  history: {
    title: 'Attendance History',
    filterByMonth: 'Filter by Month',
    clockIn: 'Clock In',
    clockOut: 'Clock Out',
    duration: 'Duration',
    status: 'Status',
    location: 'Location',
    photo: 'Photo',
    noHistory: 'No attendance history found',
    export: 'Export',
    summary: 'Monthly Summary',
    totalDays: 'Total Days',
    presentDays: 'Present Days',
    absentDays: 'Absent Days',
    totalHours: 'Total Hours',
    averageHours: 'Average Hours/Day',
    lateArrivals: 'Late Arrivals',
    earlyDepartures: 'Early Departures',
    viewDetails: 'View Details',
  },

  // ── Admin Dashboard ───────────────────────────────────────────────────────
  adminDashboard: {
    title: 'Dashboard',
    totalEmployees: 'Total Employees',
    presentToday: 'Present Today',
    onLeave: 'On Leave Today',
    pendingApprovals: 'Pending Approvals',
    attendanceOverview: 'Attendance Overview',
    leaveOverview: 'Leave Overview',
    overtimeOverview: 'Overtime Overview',
    recentActivity: 'Recent Activity',
    quickStats: 'Quick Stats',
    absentToday: 'Absent',
    lateToday: 'Late',
    clockedIn: 'Clocked In',
    notClockedIn: 'Not Clocked In',
    pendingLeaves: 'Pending Leave Requests',
    pendingOvertime: 'Pending Overtime',
    thisMonth: 'This Month',
    totalPayroll: 'Total Payroll',
    systemStatus: 'System Status',
    autoApprovalOn: 'Auto-Approval On',
    autoApprovalOff: 'Auto-Approval Off',
    liveMap: 'Live Map',
    viewAll: 'View all',
    noActivity: 'No recent activity',
    withActiveSessions: 'with active sessions',
    attendanceRate: 'Attendance rate',
    todayAttendance: "Today's Attendance",
    workforceSnapshot: 'Workforce Snapshot',
    activeSessions: 'Active sessions',
    requestsNeedReview: 'requests need review',
    actionClockIn: 'clocked in',
    actionClockOut: 'clocked out',
    actionBreakStart: 'started break',
    actionBreakEnd: 'ended break',
    actionLocationUpdate: 'updated location',
  },

  // ── Admin Employees ───────────────────────────────────────────────────────
  employees: {
    title: 'Employees',
    addEmployee: 'Add Employee',
    editEmployee: 'Edit Employee',
    deleteEmployee: 'Delete Employee',
    employeeDetails: 'Employee Details',
    searchEmployees: 'Search employees…',
    filterByDepartment: 'Filter by Department',
    filterByStatus: 'Filter by Status',
    employeeId: 'Employee ID',
    name: 'Name',
    email: 'Email',
    department: 'Department',
    position: 'Position',
    salary: 'Salary',
    hireDate: 'Hire Date',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    probationary: 'Probationary',
    actions: 'Actions',
    noEmployees: 'No employees found',
    confirmDelete: 'Are you sure you want to delete this employee?',
    deleteWarning: 'This action cannot be undone.',
    employeeAdded: 'Employee added successfully',
    employeeUpdated: 'Employee updated successfully',
    employeeDeleted: 'Employee deleted successfully',
    totalEmployees: 'Total Employees',
    activeEmployees: 'Active Employees',
    viewProfile: 'View Profile',
    sendNotification: 'Send Notification',
    resetPassword: 'Reset Password',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    nationalId: 'National ID',
    workingHoursConfig: 'Working Hours Config',
    probationEnd: 'Probation End',
    description: 'Manage employee accounts, profiles, and access permissions',
    addEmployeeDesc: 'Create a new employee account with all necessary details',
    editEmployeeDesc: 'Update employee information and credentials',
    filterByDepartment: 'All Departments',
    filterByStatus: 'All Status',
  },

  // ── Admin Attendance ──────────────────────────────────────────────────────
  adminAttendance: {
    title: 'Attendance',
    pendingRequests: 'Pending Requests',
    allRecords: 'All Records',
    approveRequest: 'Approve Request',
    rejectRequest: 'Reject Request',
    bulkApprove: 'Bulk Approve',
    approvalNotes: 'Approval Notes',
    rejectionReason: 'Rejection Reason',
    requestDetails: 'Request Details',
    employee: 'Employee',
    type: 'Type',
    timestamp: 'Timestamp',
    location: 'Location',
    photo: 'Photo',
    gpsAccuracy: 'GPS Accuracy',
    failedConstraints: 'Failed Constraints',
    notes: 'Notes',
    systemGenerated: 'System Generated',
    autoClockOut: 'Auto Clock-Out',
    clockIn: 'Clock In',
    clockOut: 'Clock Out',
    filterByEmployee: 'Filter by Employee',
    filterByDate: 'Filter by Date',
    filterByType: 'Filter by Type',
    noRequests: 'No attendance requests found',
    approved: 'Approved',
    rejected: 'Rejected',
    pending: 'Pending',
    viewPhoto: 'View Photo',
    openMap: 'Open Map',
    description: 'Monitor and manage employee attendance requests',
    totalRecords: 'Total Records',
    allDepartments: 'All Departments',
    allStatuses: 'All Statuses',
    autoApproved: 'Auto Approved',
    searchPlaceholder: 'Name or ID…',
    showing: 'Showing {from}–{to} of {total}',
    rejectRequest: 'Reject Attendance Request',
    rejectReason: 'Provide a reason for rejecting this request.',
    autoClockOutDesc: 'Grace period expired after working hours. No employee action was taken.',
    requestedTime: 'Requested Time',
    payrollTimeCapped: 'Payroll Time (Capped)',
    source: 'Source',
    systemAutoClockOut: 'System (Auto Clock-Out)',
    noAddress: 'No address recorded',
    addNotes: 'Add notes…',
    rejectionPlaceholder: 'Reason for rejection…',
    confirmReject: 'Confirm Reject',
    unknown: 'Unknown',
    failedConstraintsTitle: 'Auto-Approval Failed — Constraints',
    adminNotesTitle: 'Admin Notes',
    by: 'By',
  },

  // ── Admin Leave ───────────────────────────────────────────────────────────
  adminLeave: {
    title: 'Leave Management',
    pendingLeaves: 'Pending Leaves',
    allLeaves: 'All Leaves',
    employee: 'Employee',
    leaveType: 'Leave Type',
    startDate: 'Start Date',
    endDate: 'End Date',
    days: 'Days',
    reason: 'Reason',
    status: 'Status',
    document: 'Document',
    viewDocument: 'View Document',
    approveLeave: 'Approve Leave',
    rejectLeave: 'Reject Leave',
    approvalNotes: 'Approval Notes',
    rejectionReason: 'Rejection Reason',
    noLeaves: 'No leave requests found',
    systemGenerated: 'System-Generated',
    shortLeave: 'Short Leave',
    filterByEmployee: 'Filter by Employee',
    filterByStatus: 'Filter by Status',
    filterByType: 'Filter by Type',
    leaveBalances: 'Leave Balances',
    adjustBalance: 'Adjust Balance',
    available: 'Available',
    used: 'Used',
    description: 'Review and act on employee leave requests',
    needsReview: 'needs review',
    employeesOut: 'employees out',
    approvedToday: 'Approved Today',
    thisSession: 'this session',
    withConflicts: 'With Conflicts',
    schedulingConflicts: 'scheduling conflicts',
    searchPlaceholder: 'Name, department, ID…',
    allTypes: 'All Types',
    selected: 'selected',
    approveAll: 'Approve All',
  },

  // ── Admin Payroll ─────────────────────────────────────────────────────────
  payrollPage: {
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    prevYear: 'Previous year',
    nextYear: 'Next year',
    thisMonth: 'This month',
    stateNone: 'Not generated',
    stateDraft: 'Draft',
    stateFinalized: 'Finalized',
    statePaid: 'Paid',
    recordsCount: '{n} records',
    searchPlaceholder: 'Search employee name or code',
    allStatuses: 'All statuses',
    generateMonth: 'Generate {month}',
    regenerateDrafts: 'Regenerate drafts',
    finalizeAll: 'Finalize all drafts ({n})',
    markAllPaid: 'Mark all paid ({n})',
    loading: 'Loading payroll records…',
    emptyTitle: 'No payroll for {month}',
    emptyDescription: 'Generate payroll for this month to see it here.',
    emptyFiltered: 'No records match the current search or status filter.',
    clearFilters: 'Clear filters',
    totals: 'Totals',
    pageOf: 'Page {page} of {pages} ({total} records)',
    prev: 'Prev',
    next: 'Next',
    adjustment: 'Adjustment',
    missingTitle: '{n} active employee(s) have no payroll for {month}',
    generateMissing: 'Generate for them',
    actionView: 'View details',
    actionRegenerate: 'Regenerate',
    actionFinalize: 'Finalize',
    actionMarkPaid: 'Mark as paid',
    actionDelete: 'Delete',
    generateTitle: 'Generate payroll',
    generateHint: 'Existing drafts for this month are recalculated. Finalized and paid payrolls are never changed.',
    resultTitle: 'Payroll for {month}',
    resultCreated: '{n} created',
    resultUpdated: '{n} recalculated',
    resultSkipped: '{n} skipped (already finalized or paid)',
    resultErrors: '{n} failed',
    resultNothing: 'Nothing to generate.',
    dismiss: 'Dismiss',
    confirm: 'Confirm',
    confirmFinalizeTitle: 'Finalize payroll?',
    confirmFinalizeOne: 'Finalize the payroll of {name} for {month}? It can no longer be regenerated or deleted, and the employee is notified that the payslip is ready.',
    confirmFinalizeAll: 'Finalize {n} draft payroll(s) for {month}? They can no longer be regenerated or deleted, and each employee is notified.',
    confirmPaidTitle: 'Mark as paid?',
    confirmPaidOne: 'Mark the payroll of {name} for {month} as paid?',
    confirmPaidAll: 'Mark {n} finalized payroll(s) for {month} as paid?',
    confirmDeleteTitle: 'Delete draft?',
    confirmDeleteOne: 'Delete the draft payroll of {name} for {month}?',
    confirmRegenerateTitle: 'Regenerate drafts?',
    confirmRegenerateAll: 'Recalculate every draft for {month} from the latest attendance, leave and overtime data? Finalized and paid payrolls are not touched.',
    confirmRegenerateOne: 'Recalculate the draft payroll of {name} for {month}?',
    finalizedOk: 'Payroll finalized',
    finalizedAllOk: '{n} payroll(s) finalized',
    paidOk: 'Payroll marked as paid',
    paidAllOk: '{n} payroll(s) marked as paid',
    deletedOk: 'Draft deleted',
    bulkSomeFailed: '{n} could not be updated',
    loadFailed: 'Failed to load payroll data',
    actionFailed: 'Action failed',
    detailsTitle: 'Payroll details',
    baseSalary: 'Base salary',
    overtimePay: 'Overtime pay',
    totalDeductions: 'Total deductions',
    netPay: 'Net pay',
    overtimeSessions: 'Overtime sessions ({n}), {h}h total',
    total: 'Total',
    absentDays: 'Absent days',
    lateArrivals: 'Late arrivals',
    shortLeaves: 'Short leaves',
    earlyDepartures: 'Early departures',
    leaveBalances: 'Leave balances ({year})',
    days: 'days',
    allocatedPlusCarry: '{a} allocated + {c} carried over',
    generatedBy: 'Generated by',
    finalizedBy: 'Finalized by',
    paidBy: 'Paid by',
    on: 'on',
    auditTrail: 'Audit trail ({n})',
    auditAction: 'Action',
    auditBy: 'By',
    auditTime: 'Time',
    auditSalary: 'Net pay change',
    noChange: 'No change',
    manualAdjustment: 'Manual adjustment',
    edit: 'Edit',
    amount: 'Amount',
    reason: 'Reason',
    amountLabel: 'Amount ({c}), negative = deduction',
    amountPlaceholder: 'e.g. 500 or -200',
    reasonPlaceholder: 'e.g. Performance bonus',
    save: 'Save',
    cancel: 'Cancel',
    amountInvalid: 'Amount must be a valid number',
    reasonRequired: 'Reason is required',
    adjustmentSaved: 'Adjustment saved',
    auditActions: { generated: 'Generated', updated: 'Recalculated', finalized: 'Finalized', unfinalized: 'Reopened', paid: 'Paid', adjustment_added: 'Adjustment' },
    detailsFailed: 'Failed to load payroll details',
  },
  adminPayroll: {
    title: 'Payroll',
    description: 'Generate and manage employee monthly payroll',
    generatePayroll: 'Generate Payroll',
    generateAll: 'Generate All',
    generateForEmployee: 'Generate for Employee',
    period: 'Period',
    month: 'Month',
    year: 'Year',
    employee: 'Employee',
    basicSalary: 'Basic Salary',
    netPay: 'Net Pay',
    grossPay: 'Gross Pay',
    totalDeductions: 'Total Deductions',
    status: 'Status',
    draft: 'Draft',
    finalized: 'Finalized',
    paid: 'Paid',
    viewDetails: 'View Details',
    downloadPDF: 'Download PDF',
    markAsPaid: 'Mark as Paid',
    finalize: 'Finalize',
    manualAdjustment: 'Manual Adjustment',
    adjustmentAmount: 'Adjustment Amount',
    adjustmentReason: 'Adjustment Reason',
    noPayroll: 'No payroll records found',
    generating: 'Generating…',
    generated: 'Payroll generated successfully',
    deductionBreakdown: 'Deduction Breakdown',
    overtimePay: 'Overtime Pay',
    absentDeduction: 'Absent Deduction',
    lateDeduction: 'Late Arrival Deduction',
    shortLeaveDeduction: 'Short Leave Deduction',
    earlyDepartureDeduction: 'Early Departure Deduction',
    salaryBased: 'Salary-Based Payroll',
    hourlyBased: 'Hourly-Based Payroll',
    comingSoon: 'Soon',
    totalEmployees: 'Total Employees',
    totalPayroll: 'Total Payroll',
    afterDeductions: 'After deductions',
    avgSalary: 'Avg Salary',
    totalOvertime: 'Total Overtime',
    year: 'Year',
    month: 'Month',
    employee: 'Employee',
    period: 'Period',
    basicSalary: 'Base Salary',
    netPay: 'Net Pay',
    optionalAll: 'optional — blank = all',
    allEmployees: 'All Employees',
  },

  // ── Settings ──────────────────────────────────────────────────────────────
  settings: {
    title: 'Settings',
    companyInfo: 'Company Information',
    companyName: 'Company Name',
    companyAddress: 'Company Address',
    companyPhone: 'Company Phone',
    companyWebsite: 'Company Website',
    companyLogo: 'Company Logo',
    uploadLogo: 'Upload Logo',
    removeLogo: 'Remove Logo',
    autoApproval: 'Auto-Approval',
    autoApprovalEnabled: 'Auto-Approval Enabled',
    recklessMode: 'Reckless Mode',
    recklessModeDesc: 'Skips zone and time window checks',
    gpsThreshold: 'GPS Accuracy Threshold',
    saveSettings: 'Save Settings',
    settingsSaved: 'Settings saved successfully',
    systemSettings: 'System Settings',
    notifications: 'Notifications',
    security: 'Security',
    language: 'Language',
    theme: 'Theme',
    timezone: 'Timezone',
    companyInfoDesc: 'Shown on all PDFs — payslips, reports, and exports',
    noLogo: 'No logo',
    uploading: 'Uploading…',
    logoHint: 'PNG or JPG, max 2 MB. Displayed in the PDF header alongside the company name.',
    companyNamePlaceholder: 'Enter company name',
    saveCompany: 'Save Company Settings',
    currencyConfig: 'Currency Configuration',
    currencyDesc: 'Select the currency to be used throughout the system for salary and monetary displays',
    systemCurrency: 'System Currency',
    currencyUpdated: 'Currency updated successfully!',
    additionalSettings: 'Additional Settings',
    additionalSettingsDesc: 'More system settings will be available here',
    additionalSettingsHint: 'Working hours, auto-approval, and other settings can be configured in their respective sections.',
  },

  // ── Notifications ─────────────────────────────────────────────────────────
  notifications: {
    title: 'Notifications',
    markAsRead: 'Mark as read',
    markAllRead: 'Mark all as read',
    noNotifications: 'No notifications',
    leaveApproved: 'Leave request approved',
    leaveRejected: 'Leave request rejected',
    leaveSubmitted: 'New leave request submitted',
    overtimeApproved: 'Overtime request approved',
    overtimeRejected: 'Overtime request rejected',
    attendanceApproved: 'Attendance approved',
    attendanceRejected: 'Attendance rejected',
    payslipReady: 'Your payslip is ready',
    viewAll: 'View all',
  },

  // ── Get the App ────────────────────────────────────────────────────────────
  getApp: {
    title: 'Get the App',
    subtitle: 'Download the ExoTrack mobile app for Android',
    downloadApk: 'Download APK',
    version: 'Version',
    fileSize: 'File Size',
    requirements: 'Requirements',
    howToInstall: 'How to Install',
    step1: 'Download the APK file',
    step2: 'Allow installation from unknown sources',
    step3: 'Open the downloaded file and install',
    step4: 'Launch ExoTrack and sign in',
    features: 'Mobile Features',
    clockInOut: 'Clock in/out with GPS & photo',
    requestLeave: 'Request leave on the go',
    notifications: 'Real-time push notifications',
    viewPayslips: 'View your payslips',
  },

  // ── Sign out confirmation ─────────────────────────────────────────────────
  signOut: {
    title: 'Sign Out',
    message: 'Are you sure you want to sign out?',
    confirm: 'Sign Out',
    cancel: 'Cancel',
  },

  // ── Landing Page ──────────────────────────────────────────────────────────
  landing: {
    heroTitle: 'Smart HR & Payroll',
    heroSubtitle: 'Built for modern teams',
    heroDesc: 'Real-time attendance tracking, smart leave management, overtime approvals, and automated payroll — all in one platform.',
    getStarted: 'Get Started',
    learnMore: 'Learn More',
    featuresTitle: 'Everything you need',
    featuresSubtitle: 'A complete HR platform for your team',
    feat1Title: 'Attendance Tracking',
    feat1Desc: 'GPS-verified clock in/out with photo capture and real-time location monitoring.',
    feat2Title: 'Leave Management',
    feat2Desc: 'Streamlined leave requests with approval workflows and automatic balance tracking.',
    feat3Title: 'Overtime Control',
    feat3Desc: 'Clock-based overtime sessions with cost preview and payroll integration.',
    feat4Title: 'Payroll Automation',
    feat4Desc: 'Automatic deductions for absences, late arrivals, and early departures.',
    feat5Title: 'Reports & Analytics',
    feat5Desc: 'Detailed reports exportable to PDF, Excel, and CSV.',
    feat6Title: 'Mobile App',
    feat6Desc: 'Native Android app for employees to clock in/out on the go.',
    ctaTitle: 'Ready to get started?',
    ctaDesc: 'Sign in to your account to access the full platform.',
    signIn: 'Sign In',
    footerRights: 'All rights reserved.',
    poweredBy: 'Powered by Exodus Software',
    systemOperational: 'All systems operational',
  },

  // ── Employee Dashboard (extended) ─────────────────────────────────────────
  empDashboard: {
    loading: 'Loading your dashboard…',
    online: 'Online',
    offline: 'Offline',
    appBannerTitle: 'The ExoTrack Android app is here!',
    appBannerDesc: 'Clock in, request leave, and get push notifications right from your phone.',
    getApp: 'Get the app',
    getIt: 'Get it',
    since: 'Since',
    started: 'started',
    lastWorked: 'Last worked',
    readyToStart: 'Ready to start your day',
    today: 'Today',
    thisWeek: 'This Week',
    clockOut: 'Clock Out',
    clockIn: 'Clock In',
    dailySummary: 'Daily summary',
    hoursWorked: 'Hours worked',
    breaks: 'Breaks',
    location: 'Location',
    viewHistory: 'View history',
    leaveBalance: 'Leave Balance',
    availableDays: 'Available days',
    annualLeave: 'Annual leave',
    sickLeave: 'Sick leave',
    personal: 'Personal',
    requestLeave: 'Request leave',
    thisMonth: 'This Month',
    workStatistics: 'Work statistics',
    totalHours: 'Total hours',
    daysWorked: 'Days worked',
    avgPerDay: 'Avg / day',
    ofMonthlyTarget: '% of monthly target',
    recentAttendance: 'Recent Attendance',
    viewAll: 'View all',
    noAttendanceYet: 'No attendance records yet',
    clockInNow: 'Clock in now',
    inProgress: 'In progress',
    allCaughtUp: "You're all caught up",
    working: 'Working',
    days: 'days',
  },

  // ── Employee Attendance (extended) ────────────────────────────────────────
  empAttendance: {
    regularClock: 'Regular Clock',
    overtimeClock: 'Overtime Clock',
    workingHours: 'Working Hours',
    standardHours: 'Standard hours',
    standardHoursValue: '8:00 AM – 5:00 PM',
    weeklyHours: 'Weekly hours',
    weeklyHoursValue: '40 hours',
    quickLinks: 'Quick Links',
    viewHistory: 'View History',
    leaveRequests: 'Leave Requests',
    overtimeActive: 'Overtime Active',
    overtimePending: 'Overtime Pending Approval',
    overtimeUnderReview: 'Overtime Under Review',
    currentlyWorking: 'Currently Working',
    pendingApproval: 'Pending Approval',
    workCompleted: 'Work Completed',
    notClockedIn: 'Not Clocked In',
    remaining: 'remaining',
    timerExpired: 'Timer expired',
    rateMultiplier: 'Rate multiplier',
  },

  // ── Employee History (extended) ───────────────────────────────────────────
  empHistory: {
    regularAttendance: 'Regular Attendance',
    overtime: 'Overtime',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    thisQuarter: 'This Quarter',
    totalSessions: 'Total Sessions',
    avgDaily: 'Avg Daily',
    dateRange: 'Date Range',
    allRecords: 'All Records',
    completed: 'Completed',
    inProgress: 'In Progress',
    customRange: 'Custom Range',
    resetFilters: 'Reset Filters',
    startDate: 'Start Date',
    endDate: 'End Date',
    workSessions: 'Work Sessions',
    overtimeSessions: 'Overtime Sessions',
    showing: 'Showing',
    of: 'of',
    sessions: 'sessions',
    export: 'Export',
    date: 'Date',
    clockIn: 'Clock In',
    clockOut: 'Clock Out',
    hours: 'Hours',
    status: 'Status',
    actions: 'Actions',
    viewDetails: 'View Details',
    noSessionsFound: 'No work sessions found',
    adjustFilters: 'Try adjusting your filter criteria',
    sessionDetails: 'Work Session Details',
    timeDetails: 'Time Details',
    notRecorded: 'Not recorded',
    clockedOutBySystem: 'Clocked out by system',
    timeCapped: 'Time was capped',
    reason: 'Reason:',
    payrollFrom: 'Time counted for payroll from',
    payrollUntil: 'Time counted for payroll until',
    noClockIn: 'This session has a clock-out time but no corresponding clock-in time.',
    stillInProgress: 'This session is still in progress — no clock-out time recorded yet.',
    incompleteRecords: 'This session has incomplete time records.',
  },

  // ── Employee Leave (extended) ─────────────────────────────────────────────
  empLeave: {
    yourBalance: 'Your Leave Balance',
    newRequest: 'New Request',
    daysLeft: 'days left',
    usedOf: 'used of',
    total: 'total',
    usage: 'Usage',
    percentUsed: '% used',
    noBalanceAvailable: 'No Balance Available',
    newLeaveRequest: 'New Leave Request',
    newLeaveSubtitle: 'Apply for leave, emergency time off, or special circumstances',
    pending: 'Pending',
    approved: 'Approved',
    thisMonth: 'This Month',
    totalRequests: 'Total Requests',
    leaveHistory: 'Leave Request History',
    requests: 'requests',
    allStatus: 'All Status',
    allTypes: 'All Types',
    allTime: 'All Time',
    reset: 'Reset',
    noLeaveFound: 'No leave requests found',
    noLeaveYet: "You haven't submitted any leave requests yet.",
    submitFirst: 'Submit Your First Request',
    day: 'day',
    days: 'days',
    noReasonProvided: 'No reason provided',
    submitted: 'Submitted',
    viewDetails: 'View Details',
    cancel: 'Cancel',
    leaveDetails: 'Leave Request Details',
    startDate: 'Start Date',
    endDate: 'End Date',
    duration: 'Duration',
    reason: 'Reason',
    reviewInfo: 'Review Information',
    reviewedBy: 'Reviewed By',
    reviewDate: 'Review Date',
    reviewNotes: 'Review Notes',
    cancelRequest: 'Cancel Request',
    close: 'Close',
    annual: 'Annual',
    sick: 'Sick',
    personal: 'Personal',
  },

  // ── Employee Overtime (extended) ──────────────────────────────────────────
  empOvertime: {
    currentSession: 'Current Overtime Session',
    startedAt: 'Started at',
    on: 'on',
    duration: 'Duration:',
    status: 'Status:',
    rateMultiplier: 'Rate multiplier',
    thisMonth: 'This Month',
    pendingRequests: 'Pending Requests',
    allStatus: 'All Status',
    fromDate: 'From Date',
    toDate: 'To Date',
    apply: 'Apply',
    clear: 'Clear',
    myRequests: 'My Overtime Requests',
    requestOvertime: 'Request Overtime',
    loading: 'Loading overtime requests…',
    noRequestsFound: 'No overtime requests found',
    submitFirst: 'Submit your first overtime request to get started',
    scheduled: 'Scheduled',
    realtime: 'Real-time',
    urgent: 'URGENT',
    high: 'HIGH',
    hRequested: 'h requested',
    approvedOn: 'Approved on',
    rejected: 'Rejected:',
    showing: 'Showing',
    of: 'of',
    previous: 'Previous',
    next: 'Next',
    requestModal: 'Request Overtime',
    date: 'Date',
    startTime: 'Start Time',
    endTime: 'End Time',
    requestSummary: 'Request Summary',
    type: 'Type:',
    priorityLevel: 'Priority Level',
    low: 'Low',
    normal: 'Normal',
    highPriority: 'High',
    urgentPriority: 'Urgent',
    reasonLabel: 'Reason for Overtime',
    reasonPlaceholder: 'Please provide a clear reason for the overtime request…',
    justification: 'Additional Justification (Optional)',
    justificationPlaceholder: 'Any additional details or business justification…',
    submitRequest: 'Submit Request',
    submitting: 'Submitting…',
    detailsTitle: 'Overtime Request Details',
    requestInfo: 'Request Information',
    dateLabel: 'Date:',
    startLabel: 'Start:',
    endLabel: 'End:',
    durationLabel: 'Duration:',
    typeLabel: 'Type:',
    modeLabel: 'Mode:',
    preScheduled: 'Pre-scheduled',
    realtimeClock: 'Real-time clock',
    rejectionDetails: 'Rejection Details',
    approvalDetails: 'Approval Details',
    onLabel: 'On:',
    rejectionReason: 'Rejection reason:',
    actualTimeWorked: 'Actual Time Worked',
    actualHours: 'Actual hours:',
    cost: 'Cost:',
  },

  // ── Employee Payslips (extended) ──────────────────────────────────────────
  empPayslips: {
    year: 'Year',
    allYears: 'All Years',
    month: 'Month',
    allMonths: 'All Months',
    allStatuses: 'All Statuses',
    finalized: 'Finalized',
    paid: 'Paid',
    draft: 'Draft',
    clear: 'Clear',
    filtered: 'Filtered',
    payslipsFound: 'payslip(s) found',
    netPay: 'Net Pay',
    paidOn: 'Paid on',
    noResults: 'No results found',
    noPayslips: 'No payslips yet',
    adjustFilters: 'Try adjusting your filters.',
    noRecordsYet: 'No payroll records have been generated yet.',
    clearFilters: 'Clear filters',
  },

  // ── Employee PayslipDetail (extended) ─────────────────────────────────────
  empPayslipDetail: {
    backToPayslips: '← Back to Payslips',
    receipt: 'Receipt',
    generating: 'Generating…',
    downloadPDF: 'Download PDF',
    grossPay: 'Gross Pay',
    totalDeductions: 'Total Deductions',
    netPay: 'Net Pay',
    earnings: 'Earnings',
    baseSalary: 'Base Salary',
    workingHoursPerMonth: 'Working Hours/Month:',
    hourlyRate: 'Hourly Rate:',
    hr: 'hr',
    overtimePay: 'Overtime Pay',
    hours: 'hours',
    manualAdjustment: 'Manual Adjustment',
    deductions: 'Deductions',
    absentDays: 'Absent Days',
    lateArrivals: 'Late Arrivals',
    shortLeave: 'Short Leave',
    earlyDeparture: 'Early Departure',
    attendanceStats: 'Attendance Statistics',
    workingDays: 'Working Days',
    daysAttended: 'Days Attended',
    daysAbsent: 'Days Absent',
    onTimeArrivals: 'On-Time Arrivals',
    hoursWorked: 'Hours Worked',
    expectedHours: 'Expected Hours',
    overtimeHours: 'Overtime Hours',
    leaveBalances: 'Leave Balances',
    days: 'days',
    percentRemaining: '% remaining',
    allocated: 'Allocated:',
    used: 'Used:',
    pending: 'Pending:',
    paymentInfo: 'Payment Information',
    paymentMethod: 'Payment Method',
    bankAccount: 'Bank Account',
    paymentDate: 'Payment Date',
    paidBy: 'Paid By',
    auditTrail: 'Audit Trail',
    generated: 'Generated:',
    by: 'by',
    finalized: 'Finalized:',
    notes: 'Notes:',
  },

  // ── Employee Profile (extended) ───────────────────────────────────────────
  empProfile: {
    loading: 'Loading profile…',
    errorLoading: 'Error loading profile',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    phonePlaceholder: 'e.g. +970 59 123 4567',
    emergencyName: 'Emergency Contact Name',
    emergencyPhone: 'Emergency Contact Phone',
    accountSecurity: 'Account & Security',
    joinDate: 'Join Date',
    lastLogin: 'Last Login',
    never: 'Never',
    lastUpdated: 'Last Updated',
    accountStatus: 'Account Status',
    active: 'Active',
    inactive: 'Inactive',
    password: 'Password',
    lastChanged: 'Last changed',
    changePassword: 'Change Password',
    twoFactor: 'Two-Factor Authentication',
    twoFactorDesc: 'Additional security for your account',
    comingSoon: 'Coming Soon',
    enable2fa: 'Enable 2FA',
    twoFactorNotice: 'Two-factor authentication will be available in a future update',
    workInfo: 'Work Information',
    department: 'Department',
    manager: 'Manager',
    position: 'Position',
    status: 'Status',
    hireDate: 'Hire Date',
    canWorkField: 'Can Work Field',
    yes: 'Yes',
    no: 'No',
    leaveInfo: 'Leave Information',
    used: 'Used',
    daysRemaining: 'days remaining',
    editProfile: 'Edit Profile',
    saveChanges: 'Save Changes',
    saving: 'Saving…',
    personalInfo: 'Personal Information',
    contact: 'Contact',
  },

  // ── Employee GetTheApp (extended) ─────────────────────────────────────────
  empGetApp: {
    title: 'Get the ExoTrack App',
    subtitle: 'Clock in, request leave, and check payslips from your phone',
    back: 'Back',
    downloadAndroid: 'Download for Android',
    downloadBtn: 'Download ExoTrack v',
    version: 'Version',
    size: 'Size',
    sizeValue: '65 MB',
    requires: 'Requires',
    requiresValue: 'Android 7.0 or newer',
    checksum: 'SHA-256 checksum',
    scanQr: 'On a computer? Scan with your phone',
    qrAlt: 'QR code linking to the ExoTrack app download',
    qrHint: "Point your phone's camera at the code, the download starts on the phone, which is where the app needs to be installed.",
    howToInstall: 'How to install',
    step1Title: 'Download the APK on your Android phone',
    step1Desc: 'Use the button above (or scan the QR code from a computer). The file lands in your Downloads folder.',
    step2Title: 'Open the downloaded file',
    step2Desc: 'Tap the download notification, or find ExoTrack.apk in your Files / Downloads app and tap it.',
    step3Title: 'Allow installing from this source if asked',
    step3Desc: 'The first time, Android asks you to allow your browser to install apps. Tap Settings, switch on "Allow from this source", then go back.',
    step4Title: 'Install past the Play Protect notice',
    step4Desc: 'If Google Play Protect shows an "unrecognized app" warning, choose "Install anyway", this appears for any app not from the Play Store.',
    step5Title: 'Open ExoTrack and sign in',
    step5Desc: 'Use the same email and password you use for this website.',
  },

  // ── Reports ────────────────────────────────────────────────────────────────
  reports: {
    title: 'Reports',
    generateReport: 'Generate Report',
    reportType: 'Report Type',
    scope: 'Scope',
    individual: 'Individual',
    general: 'General (All Employees)',
    selectEmployee: 'Select Employee',
    dateRange: 'Date Range',
    exportPDF: 'Export PDF',
    exportExcel: 'Export Excel',
    exportCSV: 'Export CSV',
    attendance: 'Attendance Report',
    leave: 'Leave Report',
    overtime: 'Overtime Report',
    shortLeave: 'Short Leave Report',
    lateArrival: 'Late Arrival Report',
    payroll: 'Payroll Report',
    noData: 'No report data available',
    generating: 'Generating report…',
  },

  // ── Short Leave Sessions ───────────────────────────────────────────────────
  shortLeaveSessions: {
    title: 'Short Leave Sessions',
    description: 'Track employee short leave clock-in and clock-out events',
    sessions: 'sessions',
    totalSessions: 'total sessions',
    from: 'From',
    to: 'To',
    status: 'Status',
    allStatuses: 'All Statuses',
    active: 'Active',
    completed: 'Completed',
    expired: 'Expired',
    apply: 'Apply',
    reset: 'Reset',
    noSessions: 'No short leave sessions found for the selected period',
    employee: 'Employee',
    date: 'Date',
    leaveIn: 'Leave In',
    leaveOut: 'Leave Out',
    duration: 'Duration',
    photos: 'Photos',
    stillOut: 'Still out',
    inProgress: 'In progress',
    grace: 'Grace ✓',
    viewDetail: 'View detail',
    sessionDetail: 'Session Detail',
    gracePeriod: 'Grace — no deduction',
    leaveInPanel: 'Leave In',
    leaveOutPanel: 'Leave Out',
    time: 'Time',
    system: 'system',
    auto: 'auto',
    locationNotRecorded: 'Location not recorded',
    noPhoto: 'No photo',
    openFullMap: 'Open full map ↗',
    stillOutBadge: 'Still Out — not returned yet',
    close: 'Close',
    page: 'Page',
  },

  // ── Late Arrival Debts ────────────────────────────────────────────────────
  lateArrivalDebts: {
    title: 'Late Arrival Debts',
    description: 'Track employee late arrivals and time debts (Grace period: 8:00 AM – 8:10 AM)',
    export: 'Export',
    refresh: 'Refresh',
    totalOccurrences: 'Total Occurrences',
    employeesAffected: 'Employees Affected',
    totalHoursLate: 'Total Hours Late',
    avgMinutesLate: 'Avg Minutes Late',
    perOccurrence: 'per occurrence',
    excused: 'Excused',
    startDate: 'Start Date',
    endDate: 'End Date',
    status: 'Status',
    allStatuses: 'All Statuses',
    autoApproved: 'Auto Approved',
    pendingReview: 'Pending Review',
    search: 'Search',
    searchPlaceholder: 'Employee name or code…',
    reset: 'Reset',
    topOffenders: 'Top 5 — Most Late Arrivals',
    byDepartment: 'By Department',
    employeesAffectedLabel: 'employees affected',
    noLateArrivals: 'No late arrivals found — try adjusting your filters or date range.',
    date: 'Date',
    employee: 'Employee',
    department: 'Department',
    clockIn: 'Clock In',
    lateDuration: 'Late Duration',
    grace: 'Grace',
    by: 'by',
    excuse: 'Excuse',
    removeExcuse: 'Remove excuse',
    viewDetails: 'View details',
    delete: 'Delete',
    pageOf: 'Page {page} of {total} ({count} total)',
    excuseLateArrival: 'Excuse Late Arrival',
    excuseReason: 'Excuse Reason',
    excuseReasonRequired: 'Excuse Reason *',
    additionalNotes: 'Additional Notes',
    notesPlaceholder: 'Optional additional notes…',
    cancel: 'Cancel',
    submitExcuse: 'Excuse Late Arrival',
    lateArrivalDetails: 'Late Arrival Details',
    timeDetails: 'Time Details',
    scheduledStart: 'Scheduled Start',
    gracePeriodEnd: 'Grace Period End',
    actualClockIn: 'Actual Clock In',
    lateDurationTitle: 'Late Duration',
    excuseInfo: 'Excuse Information',
    excuseReasonLabel: 'Excuse Reason',
    excusedBy: 'Excused By',
    excusedAt: 'Excused At',
    notes: 'Notes',
    minutes: 'minutes',
    totalHours: 'h total',
    of: 'of',
    confirmRemoveExcuse: 'Are you sure you want to remove the excuse from this late arrival?',
    confirmDelete: 'Are you sure you want to delete this late arrival record for {name}?',
  },

  // ── Holiday Management ────────────────────────────────────────────────────
  holidays: {
    title: 'Holiday Management',
    description: 'Manage yearly holidays and company leave policies',
    export: 'Export',
    import: 'Import',
    addHoliday: 'Add Holiday',
    year: 'Year',
    totalHolidays: 'Total Holidays',
    upcoming: 'Upcoming',
    longWeekends: 'Long Weekends',
    allCategories: 'All',
    holidaysFor: 'Holidays for',
    noHolidays: 'No holidays found',
    noHolidaysDesc: 'Add a holiday or adjust your filters.',
    editHoliday: 'Edit Holiday',
    deleteHoliday: 'Delete Holiday',
    holidayName: 'Holiday Name',
    holidayDate: 'Date',
    category: 'Category',
    description: 'Description',
    optional: '(optional)',
    isRecurring: 'Recurring annually',
    affectsPayroll: 'Affects payroll',
    addHolidayTitle: 'Add New Holiday',
    editHolidayTitle: 'Edit Holiday',
    confirmDelete: 'Are you sure you want to delete this holiday?',
    save: 'Save',
    cancel: 'Cancel',
  },

  // ── Working Hours Config ──────────────────────────────────────────────────
  workingHours: {
    title: 'Working Hours Config',
    description: 'Configure standard working hours, overtime policies, and department-specific settings',
    refresh: 'Refresh',
    saveChanges: 'Save Changes',
    saving: 'Saving…',
    neverSaved: 'Never saved',
    updated: 'Updated',
    systemSettings: 'System Settings',
    timezone: 'Timezone',
    autoClockOut: 'Auto Clock-out',
    enableAutoClockOut: 'Enable automatic clock-out',
    enableAutoClockOutDesc: 'Clock out employees at end of working hours',
    gracePeriod: 'Grace Period (minutes)',
    gracePeriodDesc: 'Allow employees to work beyond standard hours before auto clock-out',
    weeklySchedule: 'Weekly Schedule',
    scheduleNote: 'Configure standard work days and hours for each day of the week',
    workDay: 'Work Day',
    startTime: 'Start Time',
    endTime: 'End Time',
    breakDuration: 'Break Duration',
    overtime: 'Overtime Settings',
    overtimeNote: 'Settings for overtime clock sessions',
    maxOvertimeHours: 'Max Overtime Hours/Day',
    minOvertimeMinutes: 'Min Overtime Minutes',
    overtimeRateMultiplier: 'Rate Multiplier',
    requireApproval: 'Require Prior Approval',
    configSaved: 'Working hours configuration saved',
    configSaveFailed: 'Failed to save configuration',
  },

  // ── Department Management ─────────────────────────────────────────────────
  departments: {
    title: 'Department Management',
    description: 'Manage departments, assign managers, and configure working hours',
    addDepartment: 'Add Department',
    search: 'Search',
    searchPlaceholder: 'Search departments…',
    status: 'Status',
    allDepartments: 'All Departments',
    activeOnly: 'Active Only',
    inactiveOnly: 'Inactive Only',
    reset: 'Reset',
    departmentsCount: 'Departments ({count})',
    noDepartments: 'No departments found',
    noDepartmentsDesc: 'Try adjusting your search filters.',
    manager: 'Manager',
    employees: 'Employees',
    budget: 'Budget',
    workingHours: 'Working Hours',
    notAssigned: 'Not assigned',
    notSet: 'Not set',
    notConfigured: 'Not configured',
    active: 'Active',
    inactive: 'Inactive',
    edit: 'Edit',
    deactivate: 'Deactivate',
    delete: 'Delete',
    addDepartmentTitle: 'Add Department',
    editDepartmentTitle: 'Edit Department',
    name: 'Department Name',
    code: 'Code',
    descriptionLabel: 'Description',
    email: 'Email',
    phone: 'Phone',
    save: 'Save',
    cancel: 'Cancel',
    confirmDelete: 'Are you sure you want to delete or deactivate this department?',
  },

  // ── Leave Types Management ────────────────────────────────────────────────
  leaveTypes: {
    title: 'Leave Types',
    description: 'Configure leave categories, accrual rules, and policies',
    addLeaveType: 'Add Leave Type',
    search: 'Search',
    searchPlaceholder: 'Search leave types…',
    status: 'Status',
    allTypes: 'All Types',
    activeOnly: 'Active Only',
    inactiveOnly: 'Inactive Only',
    reset: 'Reset',
    leaveTypesCount: 'Leave Types ({count})',
    noLeaveTypes: 'No leave types found',
    noLeaveTypesDesc: 'Add a leave type to get started.',
    maxPerYear: 'Max / Year',
    unlimited: 'Unlimited',
    carryForward: 'Carry Forward',
    upTo: 'Up to {n}d',
    accrualRate: 'Accrual Rate',
    manual: 'Manual',
    totalRequests: 'Total Requests',
    active: 'Active',
    inactive: 'Inactive',
    edit: 'Edit',
    delete: 'Delete',
    days: 'days',
    dPerMonth: 'd/mo',
    addLeaveTypeTitle: 'Add Leave Type',
    editLeaveTypeTitle: 'Edit Leave Type',
    name: 'Name',
    code: 'Code',
    color: 'Color',
    descriptionLabel: 'Description',
    save: 'Save',
    cancel: 'Cancel',
    confirmDelete: 'Are you sure you want to delete this leave type?',
  },

  // ── Balance Banks Management ──────────────────────────────────────────────
  balanceBanks: {
    title: 'Balance Banks',
    description: 'Manage shared leave balance pools and allocation rules',
    addBank: 'Add Balance Bank',
    totalBanks: 'Total Banks',
    totalAllocated: 'Total Allocated',
    totalUsed: 'Total Used',
    totalRemaining: 'Total Remaining',
    noBalanceBanks: 'No balance banks found',
    noBalanceBanksDesc: 'Create your first balance bank to get started.',
    employees: 'Employees',
    allocated: 'Allocated',
    used: 'Used',
    remaining: 'Remaining',
    leaveTypes: 'Leave Types',
    usage: 'Usage',
    system: 'System',
    edit: 'Edit',
    delete: 'Delete',
    days: 'days',
    addBankTitle: 'Add Balance Bank',
    editBankTitle: 'Edit Balance Bank',
    tenureBased: 'Tenure Based',
    fixed: 'Fixed',
    flexible: 'Flexible',
    save: 'Save',
    cancel: 'Cancel',
    confirmDelete: 'Are you sure you want to delete this balance bank?',
  },

  // ── Leave Balance Management ──────────────────────────────────────────────
  leaveBalance: {
    title: 'Leave Balance Management',
    description: 'Manage employee leave balances and policies',
    bulkUpdate: 'Bulk Update',
    leavePolicies: 'Leave Policies',
    export: 'Export',
    manualEditInfo: 'Manually modify used days for any employee\'s balance bank. Changes automatically update available balances and are tracked in the audit history.',
    totalEmployees: 'Total Employees',
    totalLeaveDays: 'Total Leave Days',
    usedLeaveDays: 'Used Leave Days',
    lowBalance: 'Low Balance',
    department: 'Department',
    allDepartments: 'All Departments',
    leaveType: 'Leave Type',
    allTypes: 'All Types',
    balanceStatus: 'Balance Status',
    allBalances: 'All Balances',
    low: 'Low (< 5 days)',
    medium: 'Medium (5–15 days)',
    high: 'High (> 15 days)',
    applyFilters: 'Apply Filters',
    reset: 'Reset',
    employeeLeaveBalances: 'Employee Leave Balances',
    searchPlaceholder: 'Search employees…',
    noEmployees: 'No employees found',
    noEmployeesDesc: 'Try adjusting your search or filters.',
    employee: 'Employee',
    allocated: 'Allocated',
    used: 'Used',
    remaining: 'Remaining',
    actions: 'Actions',
    adjust: 'Adjust',
  },

  // ── Overtime Clock Management ─────────────────────────────────────────────
  overtimeClock: {
    title: 'Overtime Clock Management',
    description: 'Review and manage employee overtime clock sessions',
    refresh: 'Refresh',
    pendingApproval: 'Pending Approval',
    activeSessions: 'Active Sessions',
    needsReview: 'Needs Review',
    pendingCost: 'Pending Cost',
    allClear: 'All Clear!',
    nothingToReview: 'No sessions need review at the moment',
    noActiveSessions: 'No Active Sessions',
    noActiveSessionsDesc: 'There are no active overtime sessions running',
    autoExpired: 'Auto-Expired',
    clockIn: 'Clock In',
    clockOut: 'Clock Out',
    timerEnds: 'Timer Ends',
    elapsed: 'Elapsed',
    estCost: 'Est. Cost',
    duration: 'Duration',
    rate: 'Rate',
    anomalies: 'anomalies',
    view: 'View',
    edit: 'Edit',
    approve: 'Approve',
    reject: 'Reject',
    forceClose: 'Force Close',
    statusFilter: 'Status',
    allStatuses: 'All Statuses',
    pending: 'Pending',
    active: 'Active',
    underReview: 'Under Review',
    approved: 'Approved',
    rejected: 'Rejected',
    expired: 'Expired',
    fromDate: 'From',
    toDate: 'To',
    clear: 'Clear',
    noRequests: 'No requests found',
    employee: 'Employee',
    type: 'Type',
    status: 'Status',
    cost: 'Cost',
    page: 'Page',
    of: 'of',
  },

  // ── Overtime Management (Legacy) ──────────────────────────────────────────
  overtimeManagement: {
    title: 'Overtime Management',
    description: 'Review and approve employee overtime requests',
    bulkApprove: 'Bulk Approve ({count})',
    bulkReject: 'Bulk Reject ({count})',
    export: 'Export',
    pendingRequests: 'Pending Requests',
    monthlyOvertime: 'This Month\'s Overtime',
    overtimeCost: 'Overtime Cost',
    activeSessions: 'Active Sessions',
    status: 'Status',
    allStatuses: 'All Statuses',
    department: 'Department',
    allDepartments: 'All Departments',
    from: 'From',
    to: 'To',
    apply: 'Apply',
    overtimeRequests: 'Overtime Requests',
    selectAll: 'Select All',
    noRequests: 'No overtime requests found',
    ongoing: 'Ongoing',
    priority: 'Priority',
    showing: 'Showing {from}–{to} of {total}',
    detailsTitle: 'Overtime Request Details',
    employeeInfo: 'Employee Information',
    requestDetails: 'Request Details',
    name: 'Name',
    department: 'Department',
    employeeId: 'Employee ID',
    hourlyRate: 'Hourly Rate',
    date: 'Date',
    expectedDuration: 'Expected Duration',
    startTime: 'Start Time',
    status: 'Status',
    mode: 'Mode',
    type: 'Type',
    preScheduled: 'Pre-scheduled',
    realtimeClock: 'Real-time clock',
    clockInLabel: 'Clock In',
    clockOutLabel: 'Clock Out',
    clockOutMethod: 'Clock Out Method',
    actualDuration: 'Actual Duration',
    estimatedCost: 'Estimated Cost',
    actualCost: 'Actual Cost',
    manual: 'Manual (Employee)',
    autoCapped: 'Auto-Capped',
    adminForce: 'Admin Force Close',
    reasonLabel: 'Reason',
    approvalDecision: 'Approval Decision',
    notes: 'Notes',
    notesPlaceholder: 'Add any notes or conditions…',
    cancel: 'Cancel',
    submitDecision: 'Submit Decision',
    submitting: 'Submitting…',
    bulkApproveTitle: 'Bulk Approve Requests',
    bulkApproveDesc: 'You are about to approve {count} overtime requests.',
    bulkApprovalNotes: 'Bulk Approval Notes',
    bulkNotesPlaceholder: 'Add notes for all approved requests…',
    approveAll: 'Approve All',
    approving: 'Approving…',
    bulkRejectTitle: 'Bulk Reject Requests',
    min: 'min',
    hr: 'hr',
  },

  // ── Reports (extended) ────────────────────────────────────────────────────
  reportsPage: {
    title: 'Reports',
    subtitle: 'Overtime, attendance, leave, short leave, late arrivals and payroll, on screen and as PDF, Excel or CSV.',
    reportType: 'Report',
    types: { overtime: 'Overtime', attendance: 'Attendance', leave: 'Leave', 'short-leave': 'Short leave', 'late-arrivals': 'Late arrivals', payroll: 'Payroll' },
    typeDesc: {
      overtime: 'Sessions, hours and cost',
      attendance: 'Daily sheet, absences, hours',
      leave: 'Requests, balances, banks',
      'short-leave': 'Early departures and coverage',
      'late-arrivals': 'Late minutes and deductions',
      payroll: 'Monthly salary breakdown',
    },
    scope: 'Scope',
    scopes: { individual: 'One employee', general: 'All employees' },
    employee: 'Employee',
    selectEmployee: 'Select an employee…',
    period: 'Period',
    periods: { thisMonth: 'This month', lastMonth: 'Last month', thisQuarter: 'This quarter', thisYear: 'This year', lastYear: 'Last year', allTime: 'All time', custom: 'Custom range' },
    from: 'From',
    to: 'To',
    today: 'today',
    emptyTitle: 'Choose a report and press Generate',
    emptyBody: 'Pick the report type, one employee or all employees, and the period. The report opens here and can be downloaded as PDF, Excel or CSV.',
    failed: 'Failed to generate the report',
  },
  reportsExt: {
    individualReport: 'Individual Report',
    individualDesc: 'Detailed records for a single employee',
    individualDetail: 'Shows detailed records including actual clock-in/out times, overtime reasons, leave details with full audit trail',
    generalReport: 'General Report',
    generalDesc: 'Aggregated data across all employees',
    generalDetail: 'Shows team-wide summaries, department breakdowns, and comparative analysis across employees',
    selectDataType: 'Select Data Type',
    overtime: 'Overtime',
    overtimeDesc: 'Session hours, costs, and approval status',
    attendance: 'Attendance',
    attendanceDesc: 'Clock-in/out records and working hours',
    leave: 'Leave',
    leaveDesc: 'Leave requests, balances, and approvals',
    shortLeave: 'Short Leave',
    shortLeaveDesc: 'Mid-day leave sessions and durations',
    lateArrival: 'Late Arrivals',
    lateArrivalDesc: 'Late clock-ins and deduction amounts',
    payroll: 'Payroll',
    payrollDesc: 'Salary breakdowns and deduction summaries',
    configureReport: 'Configure Your Report',
    selectEmployee: 'Select Employee',
    allEmployees: 'All Employees',
    dateRange: 'Date Range',
    startDate: 'Start Date',
    endDate: 'End Date',
    generateReport: 'Generate Report',
    generating: 'Generating…',
    exporting: 'Exporting…',
    pdfExported: 'PDF report downloaded',
    rateBasis: 'Rate month',
    rateBasisHint: 'Optional: cost every session at the employee hourly rate of this payroll month. Leave empty to use the recorded cost.',
    exportPDF: 'Export PDF',
    exportExcel: 'Export Excel',
    exportCSV: 'Export CSV',
    reset: 'Reset',
  },
}
